import { titleCase } from './utils'
import moment from 'moment-timezone'
import type { IRecurrenceSchedule } from '@pushly/models/lib/interfaces/recurrence-schedule'

type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'

const sorter: Record<string, number> = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
}

const transformer: Record<string, string> = {
    monday: 'Mon',
    tuesday: 'Tues',
    wednesday: 'Wed',
    thursday: 'Thurs',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun',
}

export function buildRelativeLanguageSchedule(schedule: IRecurrenceSchedule) {
    const scheduleType = schedule?.type
    let scheduleTypeDisplay =
        scheduleType === 'daily' || scheduleType === 'weekly' ? titleCase(scheduleType) : 'Monthly'

    const allowedDays = schedule.allowed_days as DayOfWeek[] | undefined
    let allowedDaysDisplay = 'any day'

    const freq = schedule.frequency

    if (allowedDays) {
        allowedDaysDisplay = buildAllowedDays(allowedDays)
        if (freq && freq > 1) {
            scheduleTypeDisplay = `Every ${freq} ${scheduleType === 'daily' ? 'days' : 'weeks'}`
        } else {
            scheduleTypeDisplay = ``
        }
    } else if (scheduleType === 'monthly_relative') {
        const relativeType = schedule.relative_type

        if (relativeType === 'relative_day') {
            const day = schedule.relative_day?.day
            const ordinal = schedule.relative_day?.ordinal
            allowedDaysDisplay = `${ordinal} ${titleCase(day as string)}`
        } else {
            allowedDaysDisplay = `the ${relativeType!.split('_').join(' ')}`
        }
    } else if (scheduleType === 'monthly_specific') {
        const specificDay = schedule.specific_day!.toString()
        let superScript = 'th'
        switch (specificDay[specificDay.length - 1]) {
            case '1':
                superScript = 'st'
                break
            case '2':
                superScript = 'nd'
                break
            case '3':
                superScript = 'rd'
                break
            default:
                break
        }
        allowedDaysDisplay = `the ${specificDay}${superScript}`
    }

    let determiner =
        scheduleType === 'monthly_specific' && schedule.relative_type === 'relative_day'
            ? 'every'
            : scheduleType === 'daily' || scheduleType === 'weekly'
            ? `on`
            : 'on the'
    if (freq && freq === 1) {
        determiner = ''
    }

    const specificTime = moment(schedule?.specific_time, 'H:mm').format('h:mm A')

    return `${scheduleTypeDisplay} ${determiner} ${allowedDaysDisplay}${
        scheduleType === 'daily'
            ? `${
                  allowedDaysDisplay === 'Wed' || allowedDaysDisplay[allowedDaysDisplay.length - 3] === 'W'
                      ? 'nesdays'
                      : allowedDaysDisplay === 'Sat' || allowedDaysDisplay[allowedDaysDisplay.length - 3] === 'S'
                      ? 'urdays'
                      : 'days'
              }`
            : ''
    } 
        at ${specificTime}`
}

function buildAllowedDays(days: DayOfWeek[]) {
    const allowedDays = days.sort((a, b) => sorter[a] - sorter[b]).map((day) => transformer[day])
    let lastDay
    if (allowedDays.length === 7) {
        return ''
    } else {
        if (allowedDays.length > 1) {
            lastDay = allowedDays.length > 1 ? allowedDays.pop() : allowedDays[0]
        }
    }

    return allowedDays.join(', ') + (allowedDays.length > 1 ? ' and ' + lastDay : '')
}
